import { z } from 'zod'

import { isCompletePhoneNumber, phoneDigits } from './phone'

// Shared field schemas, so "Email is required." reads the same on every form.
// Messages say what to do rather than what went wrong, and name the field so
// they still make sense when a screen reader announces them on their own.

/** Letters plus the punctuation real names actually contain. */
const NAME_PATTERN = /^\p{L}+(?:[ '\-’]\p{L}+)*$/u

export function requiredText(label: string, { min = 1 }: { min?: number } = {}) {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .min(min, `${label} must be at least ${min} characters.`)
}

export function optionalText() {
  return z.string().trim().optional().or(z.literal(''))
}

export function personName(label: string) {
  return requiredText(label, { min: 2 }).refine((value) => NAME_PATTERN.test(value), {
    message: `${label} may include letters, spaces, hyphens, and apostrophes only.`,
  })
}

export function email(label = 'Email') {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .pipe(z.email({ message: 'Enter a valid email address.' }))
}

export function optionalEmail() {
  return z
    .string()
    .trim()
    .refine((value) => value === '' || z.email().safeParse(value).success, {
      message: 'Enter a valid email address.',
    })
    .optional()
}

/**
 * Phone validation runs on the digits, not the mask, so a half-typed
 * `(555) 12` fails for being short rather than for its punctuation.
 */
export function phone(label = 'Phone number') {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .refine(isCompletePhoneNumber, { message: 'Enter a 10-digit phone number.' })
}

export function optionalPhone() {
  return z
    .string()
    .trim()
    .refine((value) => phoneDigits(value).length === 0 || isCompletePhoneNumber(value), {
      message: 'Enter a 10-digit phone number.',
    })
    .optional()
}

/** A `<select>` or combobox that must hold something other than its blank option. */
export function requiredSelection(label: string) {
  return z
    .union([z.string(), z.number()])
    .refine((value) => String(value ?? '').trim().length > 0, {
      message: `${label} is required.`,
    })
}

/** A checkbox the user has to tick, such as terms acceptance. */
export function requiredCheckbox(message: string) {
  return z.boolean().refine((value) => value, { message })
}

/** Currency held as a string so cents never round-trip through a float. */
export function currency(label = 'Amount') {
  return z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .refine((value) => /^\d+(\.\d{1,2})?$/.test(value.replace(/[$,]/g, '')), {
      message: `Enter ${label.toLowerCase()} as a number, such as 25.00.`,
    })
}

export function requiredDate(label: string) {
  return z.string().trim().min(1, `${label} is required.`)
}
